<template>
  <sui-segment-group
    raised
    style="margin: auto; margin-top: 20px; max-width: 1000px"
  >
    <sui-segment
      inverted
      style="font-size: 18px; font-weight: bold; text-align: center"
    >
      Importación inicial (implementación)
    </sui-segment>

    <sui-segment style="text-align: right">
      <sui-button
        primary
        @click="downloadTemplates"
      >
        <sui-icon name="download" /> Descargar todas las plantillas
      </sui-button>
    </sui-segment>

    <sui-segment>
      <sui-message info>
        Sube los catálogos <b>en este orden</b>: cada uno depende de los anteriores
        (los productos referencian proveedores; el inventario, productos y almacenes;
        los clientes, listas de precio y almacenes). Si una referencia no existe todavía,
        se crea un registro mínimo y se marca con un aviso — así nunca se bloquea la carga.
      </sui-message>
    </sui-segment>

    <sui-segment
      v-for="cat in catalogs"
      :key="cat.key"
      style="padding: 0"
    >
      <div
        class="step-header"
        @click="toggle(cat.key)"
      >
        <span>
          <b>{{ cat.n }}. {{ cat.title }}</b>
          <span class="file-hint">{{ cat.file }}</span>
        </span>
        <span>
          <sui-icon
            v-if="done[cat.key]"
            name="check circle"
            color="green"
          />
          <sui-label
            v-if="cat.ready && isLocked(cat)"
            size="tiny"
            color="grey"
          >
            <sui-icon name="lock" /> Bloqueado
          </sui-label>
          <sui-label
            v-if="!cat.ready"
            size="tiny"
          >Pendiente</sui-label>
          <sui-icon :name="open === cat.key ? 'chevron up' : 'chevron down'" />
        </span>
      </div>

      <div
        v-if="open === cat.key"
        class="step-body"
      >
        <sui-message
          v-if="!cat.ready"
          warning
        >
          Este importador se habilitará próximamente. Sigue el mismo patrón
          (vista previa → corregir → confirmar).
        </sui-message>
        <sui-message
          v-else-if="isLocked(cat)"
          warning
        >
          <sui-icon name="lock" />
          Primero importa: <b>{{ missingReqs(cat).map(titleOf).join(", ") }}</b>.
          Este paso depende de esos catálogos.
        </sui-message>
        <CatalogImport
          v-else
          :description="cat.desc"
          :import-fn="cat.fn"
          :columns="cat.cols"
          :sheet="sheetName(cat)"
          :ai-hint="cat.aiHint || ''"
          :ai-map-to="cat.aiMapTo || ''"
          @done="onStepDone(cat)"
        />
      </div>
    </sui-segment>
  </sui-segment-group>
</template>

<script setup>
import { onMounted, reactive, ref } from "vue";
import CatalogImport from "../components/CatalogImport.vue";
import Api from "../../api/Api";
import { zipFiles } from "../../api/zip";

const open = ref("proveedores");
const done = reactive({});

const catalogs = [
    {
        n: 1, key: "proveedores", title: "Proveedores", file: "02_proveedores.csv", ready: true,
        fn: Api.importSuppliers,
        aiHint: "dias_pago debe ser un número entero de días de crédito. 'CONTADO' o 'De contado' = 1. '30 DIAS'/'30 días' = 30, '60 DIAS' = 60 (deja solo el número, sin la palabra 'días').",
        headers: ["nombre_proveedor", "dias_pago", "contacto_nombre", "contacto_apellidos", "contacto_telefono", "contacto_email", "contacto_departamento"],
        desc: "Catálogo de proveedores. Requerido antes de productos (los productos se enlazan a estos proveedores por nombre).",
        cols: [
            { key: "nombre_proveedor", label: "Nombre" },
            { key: "dias_pago", label: "Días pago" },
        ],
    },
    {
        n: 2, key: "almacenes", title: "Almacenes", file: "05_almacenes.csv", ready: true,
        fn: Api.importWarehouses,
        headers: ["nombre_almacen", "descripcion", "seccion", "calle", "numero_ext", "colonia", "ciudad", "estado", "cp", "pais"],
        desc: "Almacenes y sus secciones. Requerido antes del inventario inicial. Repite el nombre del almacén en varias filas para agregarle varias secciones.",
        cols: [
            { key: "nombre_almacen", label: "Almacén" },
            { key: "seccion", label: "Sección" },
        ],
    },
    {
        n: 3, key: "listas", title: "Listas de precio", file: "04_listas_precio.csv", ready: true,
        fn: Api.importPriceLists,
        headers: ["nombre_lista", "descuento", "min_individual", "min_grupo", "min_monto", "min_monto_grupo"],
        desc: "Listas de precio (rangos). El campo descuento es un porcentaje (0–100). Los clientes las referencian por nombre.",
        cols: [
            { key: "nombre_lista", label: "Nombre lista" },
            { key: "descuento", label: "Descuento %" },
        ],
    },
    {
        n: 4, key: "productos", title: "Productos", file: "03_productos.csv", ready: true,
        requires: ["proveedores"],
        fn: Api.importProducts,
        headers: ["nombre_producto", "tipo_producto", "codigo_upc", "clave_sat", "precio_venta", "proveedor", "precio_proveedor", "tiene_iva", "exento_iva", "tiene_ieps", "tasa_ieps", "aplica_descuento", "tiempo_caducidad_dias", "publicado", "descripcion", "id_legado", "presentacion_nombre", "unidad", "cantidad_por_presentacion"],
        desc: "Catálogo de productos. Importa proveedores primero para que se enlacen a los reales. Líneas y unidades faltantes se crean automáticamente.",
        cols: [
            { key: "nombre_producto", label: "Nombre" },
            { key: "tipo_producto", label: "Línea", bulk: true },
            { key: "clave_sat", label: "Clave SAT" },
            { key: "precio_venta", label: "Precio" },
            {
                key: "proveedor", label: "Proveedor", bulk: true,
                optionsFn: async () => {
                    const r = await Api.getSuppliers();
                    return (Array.isArray(r.data) ? r.data : []).map((s) => ({ value: s.supplier_name, label: s.supplier_name }));
                },
            },
        ],
    },
    {
        n: 5, key: "precios", title: "Precios por lista (override)", file: "07_precios_producto.csv", ready: true,
        requires: ["productos", "listas"],
        fn: Api.importProductPrices,
        aiMapTo: "product",
        aiHint: "Si falta el UPC o el nombre no coincide exactamente, mapea 'producto' al producto existente más parecido y llena 'codigo_upc' con su UPC.",
        headers: ["codigo_upc", "producto", "lista_precio", "precio"],
        desc: "Precio de venta por producto en cada lista (sobrescribe el descuento general de la lista). Requiere productos y listas de precio ya importados. El descuento se calcula desde el precio base del producto; un precio mayor al base queda como recargo (descuento negativo).",
        cols: [
            { key: "codigo_upc", label: "UPC" },
            { key: "producto", label: "Producto" },
            { key: "lista_precio", label: "Lista" },
            { key: "precio", label: "Precio" },
        ],
    },
    {
        n: 6, key: "inventario", title: "Inventario inicial", file: "06_inventario_inicial.csv", ready: true,
        requires: ["productos", "almacenes"],
        fn: Api.importInventory,
        headers: ["producto", "codigo_upc", "almacen", "seccion", "cantidad", "costo_unitario", "lote", "pedimento", "fecha_caducidad", "descripcion"],
        desc: "Existencias iniciales. Requiere que productos y almacenes ya estén importados (el producto y el almacén/sección deben existir). Vuelve a importar solo si quieres agregar más existencias — no reemplaza las anteriores.",
        cols: [
            { key: "producto", label: "Producto" },
            { key: "codigo_upc", label: "UPC" },
            { key: "almacen", label: "Almacén" },
            { key: "seccion", label: "Sección" },
            { key: "cantidad", label: "Cantidad" },
        ],
    },
    {
        n: 7, key: "clientes", title: "Clientes", file: "01_clientes.csv", ready: false,
        requires: ["listas", "almacenes"],
        headers: ["nombre_comercial", "razon_social", "rfc", "telefono", "limite_credito", "pedidos_credito", "plazo_credito_horas", "grupo", "lista_precio", "vendedor_email", "almacen", "id_legado", "calle", "numero_ext", "numero_int", "colonia", "ciudad", "estado", "cp", "pais", "factura_razon_social", "factura_rfc", "factura_email", "factura_regimen_fiscal", "factura_uso_cfdi", "factura_cp", "contacto_nombre", "contacto_apellidos", "contacto_telefono", "contacto_email", "contacto_departamento"],
    },
];

const sheetName = (cat) => cat.file.replace(/\.csv$/, "");

// Bundle the blank templates (one CSV per catalog, headers only) into a zip and download it.
// Built client-side (the api container can't reach db/onboarding-templates) with a tiny store-only
// zip writer, so no backend and no zip dependency.
const downloadTemplates = () => {
    const files = catalogs.map((c) => ({
        name: c.file,
        content: (c.headers || []).join(",") + "\n",
    }));
    const url = URL.createObjectURL(zipFiles(files));
    const a = document.createElement("a");
    a.href = url;
    a.download = "plantillas_onboarding.zip";
    a.click();
    URL.revokeObjectURL(url);
};

// Row counts per catalog (from the DB), so a step stays locked until its prerequisites have data.
const counts = reactive({});
const loadCounts = async () => {
    const r = await Api.getOnboardingCounts();
    if (!r.error && r.data) Object.assign(counts, r.data);
};
onMounted(loadCounts);

const titleOf = (key) => (catalogs.find((c) => c.key === key) || {}).title || key;
const missingReqs = (cat) => (cat.requires || []).filter((k) => !(counts[k] > 0));
const isLocked = (cat) => missingReqs(cat).length > 0;

// After a step imports, refresh counts so dependent steps unlock immediately.
const onStepDone = async (cat) => {
    done[cat.key] = true;
    await loadCounts();
};

const toggle = (key) => {
    open.value = open.value === key ? null : key;
};
</script>

<style scoped>
.step-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 16px;
    cursor: pointer;
    font-size: 15px;
}
.step-header:hover { background: #f7f7f9; }
.file-hint { color: #999; font-size: 12px; margin-left: 10px; }
.step-body { padding: 0 16px 18px; }
</style>
